import React, { useState } from 'react';
import { useFetchAllBonlivForCollection, useUpdateBonlivStatus } from '../../../hooks/bonlivHooks';
import { useFetchAllEntrepots } from '../../../hooks/entrepotHooks';
import { useSelector } from 'react-redux';
import {
  Paper,
  CircularProgress,
  Typography,
  Box,
  Container,
  Button,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Grid,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  Tabs,
  Tab,
  TextField
} from '@mui/material';
import { ExportOutlined, CheckOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

const CollectionOverviewList = ({ onViewDetails, onRefresh }) => {
  const user = useSelector(state => state.user);
  const initialEntrepot = localStorage.getItem('activeEntrepot') || (user?.CODE_ENT || 'all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeEntrepot, setActiveEntrepot] = useState(initialEntrepot);
  const [clientSearch, setClientSearch] = useState('');
  const [refDevSearch, setRefDevSearch] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  
  // Fetch available entrepots
  const { entrepots } = useFetchAllEntrepots();

  // Function to check if a bonliv is a complementary order
  const isComplementaryOrder = (remarque) => {
    if (!remarque) return false;
    // Check for patterns like @COMP123, @COM123, @CMP123, etc.
    return /@(COMP|COM|CMP)\s*\d{3,4}/i.test(remarque);
  };

  // Function to check if a devis is unique (doesn't have multiple étages)
  const isUniqueDevis = (bonliv) => {
    return !bonliv.bonlivPerEtages || bonliv.bonlivPerEtages.length <= 1;
  };
  
  const [searchTrigger, setSearchTrigger] = useState(0);
  
  const { 
    bonlivs, 
    total, 
    loading, 
    error, 
    refetch 
  } = useFetchAllBonlivForCollection({
    page: page + 1,
    pageSize: rowsPerPage,
    filter: activeFilter,
    entrepot: activeEntrepot,
    clientSearch: clientSearch || undefined,
    refDevSearch: refDevSearch || undefined,
    searchTrigger // Add this to trigger refetch
  });

  const { updateStatus } = useUpdateBonlivStatus();

  const handleMarkPrepared = async (bonlivId) => {
    try {
      setUpdatingId(bonlivId);
      await updateStatus(bonlivId, 'PREPARE');
      await refetch();
    } catch (e) {
      // Optional: add user feedback here if desired
      console.error('Failed to set PREPARE status:', e);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleEntrepotChange = (newEntrepot) => {
    setActiveEntrepot(newEntrepot);
    setPage(0);
    setSearchTrigger(prev => prev + 1); // Trigger refetch
    localStorage.setItem('activeEntrepot', newEntrepot);
  };

  const handleFilterChange = (event, newValue) => {
    setActiveFilter(newValue);
    setPage(0); // Reset to first page when changing filter
  };

  const handleClientSearchChange = (event) => {
    setClientSearch(event.target.value);
  };

  const handleRefDevSearchChange = (event) => {
    setRefDevSearch(event.target.value);
  };

  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
    setSearchTrigger(prev => prev + 1); // Trigger a new search
  };

  const handleClearSearch = () => {
    setClientSearch('');
    setRefDevSearch('');
    setPage(0);
    setSearchTrigger(prev => prev + 1); // Trigger search after clearing
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
  };

  const areAllEtagesSignale = (bonliv) => {
    if (!bonliv.bonlivPerEtages || bonliv.bonlivPerEtages.length === 0) return false;
    return bonliv.bonlivPerEtages.every(etage => etage.status === 'SIGNALE');
  };

  // Pass refetch function to parent via onRefresh prop
  React.useEffect(() => {
    console.log('CollectionOverviewList: useEffect called', { onRefresh: !!onRefresh, refetch: typeof refetch });
    if (onRefresh && typeof refetch === 'function') {
      console.log('CollectionOverviewList: Setting refetch function');
      onRefresh(refetch);
    }
  }, [onRefresh, refetch]);

  if (loading && !bonlivs?.length) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
          <Typography variant="h6" style={{ marginLeft: 16 }}>Chargement...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" flexDirection="column">
          <Typography color="error" variant="h6">Erreur lors du chargement des données:</Typography>
          <Typography color="error">{error}</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={refetch}
            style={{ marginTop: '1rem' }}
          >
            Réessayer
          </Button>
        </Box>
      </Container>
    );
  }

  const renderSearchSection = () => (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="entrepot-select-label">Entrepôt</InputLabel>
            <Select
              labelId="entrepot-select-label"
              value={activeEntrepot}
              onChange={(e) => handleEntrepotChange(e.target.value)}
              label="Entrepôt"
            >
              <MenuItem value="all">Tous les entrepôts</MenuItem>
              {entrepots?.map((entrepot) => (
                <MenuItem key={entrepot.code_ent} value={entrepot.code_ent}>
                  {entrepot.code_ent} - {entrepot.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2.5}>
          <TextField
            fullWidth
            size="small"
            label="Rechercher par client"
            variant="outlined"
            value={clientSearch}
            onChange={handleClientSearchChange}
            placeholder="Nom du client..."
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2.5}>
          <TextField
            fullWidth
            size="small"
            label="Rechercher par REF_DEV"
            variant="outlined"
            value={refDevSearch}
            onChange={handleRefDevSearchChange}
            placeholder="Référence devis..."
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2.5}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={!clientSearch && !refDevSearch}
            fullWidth
          >
            Rechercher
          </Button>
        </Grid>
        <Grid item xs={12} sm={2.5}>
          <Button
            variant="outlined"
            onClick={handleClearSearch}
            disabled={!clientSearch && !refDevSearch}
            fullWidth
          >
            Effacer recherche
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const renderFilterTabs = () => (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
      <Tabs 
        value={activeFilter} 
        onChange={handleFilterChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Tous" value="all" />
        <Tab label="AJD" value="ajd" />
        <Tab label="Unique" value="unique" />
        <Tab label="Sur Place" value="surplace" />
        <Tab label="Nos Soins" value="nossoins" />
        <Tab label="Normal" value="normal" />
      </Tabs>
    </Box>
  );

  const renderCardView = () => {
    // Apply unique filter if selected
    let filteredBonlivs = bonlivs;
    if (activeFilter === 'unique') {
      filteredBonlivs = bonlivs.filter(bonliv => isUniqueDevis(bonliv));
    }
    
    const pageCount = Math.ceil(total / rowsPerPage);

    return (
      <>
        {renderSearchSection()}
        {renderFilterTabs()}
        <Box>
          {filteredBonlivs.length > 0 ? (
            filteredBonlivs.map((bonliv) => (
              <Card 
                key={bonliv.id}
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  boxShadow: 2,
                  mb: 1.5,
                  borderLeft: areAllEtagesSignale(bonliv) ? '4px solid #4caf50' : '4px solid #f44336'
                }}
              >
                <CardContent sx={{ py: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold' }}>
                        {bonliv.REF_BL}
                      </Typography>
                      {bonliv.status === 'EN_PREPARATION' && (
                        <Chip
                          size="small"
                          color="primary"
                          label="P"
                          icon={<CheckOutlined style={{ fontSize: 12 }} />}
                          onClick={() => handleMarkPrepared(bonliv.id)}
                          disabled={updatingId === bonliv.id}
                          sx={{ height: 22 }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {bonliv.AJD && <Chip label="AJD" color="primary" size="small" />}
                      {bonliv.SUR_PLACE && <Chip label="Sur Place" color="secondary" size="small" />}
                      {bonliv.NOS_SOINS && <Chip label="Nos Soins" color="info" size="small" />}
                      {/* Unique devis badge */}
                      {isUniqueDevis(bonliv) && (
                        <Chip 
                          label="UNIQUE" 
                          color="info"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      {/* Complementary order badge */}
                      {isComplementaryOrder(bonliv.REMARQUE) && (
                        <Chip 
                          label="COMP" 
                          color="warning"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      <Tooltip title="Voir les détails pour cette préparation de Bonliv">
                        <IconButton
                          color="primary"
                          onClick={() => onViewDetails(bonliv.id)}
                          size="small"
                        >
                          <ExportOutlined />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Client:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {bonliv.CLIENT || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Articles:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {bonliv.totalItems}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Date:</Typography>
                        <Typography variant="body2">
                          {bonliv.DATE_BL ? format(new Date(bonliv.DATE_BL), 'dd/MM/yyyy') : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Mode Liv:</Typography>
                        <Typography variant="body2">
                          {bonliv.MODELIV || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Mode Paie:</Typography>
                        <Typography variant="body2">
                          {bonliv.MODE_PAIE || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {bonliv.bonlivPerEtages && bonliv.bonlivPerEtages.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">Étages:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {bonliv.bonlivPerEtages.map((etage) => (
                            <Chip
                              key={etage.Etage}
                              label={`Étage ${etage.Etage} (${etage.itemCount || 0})`}
                              color={etage.status === 'SIGNALE' ? 'success' : 'error'}
                              size="small"
                              sx={{ fontWeight: 'medium' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  )}

                  
                  
                  {bonliv.REMARQUE && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Remarque:</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {bonliv.REMARQUE}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6">Aucune donnée trouvée.</Typography>
            </Paper>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap' }}>
          <Box sx={{ my: 1 }}>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="rows-per-page-label">Par page</InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Par page"
              >
                {[10, 20, 50, 100].map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Pagination 
            count={pageCount} 
            page={page + 1} 
            onChange={handleChangePage} 
            color="primary" 
            sx={{ my: 1 }}
          />
        </Box>
      </>
    );
  };

  return (
    <Container maxWidth="xl" style={{ marginTop: '2rem' }}>
      <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>
          {renderCardView()}
        </Box>
      </Box>
    </Container>
  );
};

export default CollectionOverviewList; 